// Constants
import { ClientSurface } from "@ms/uno-constants/lib/local/AppConstants";
import { GraphResponseHeaders, LoopServiceRequestHeaders, MarsServiceRequestHeaders, RequestHeaders, } from "@ms/uno-constants/lib/local/ServiceConstants";
import { BaseServiceOrchestrator } from "./BaseServiceOrchestrator";
// Services
import { AuthenticatedAjaxClient } from "@ms/uno-services/lib/local/ajaxClients/AuthenticatedAjaxClient";
import { BasicAjaxClient } from "@ms/uno-services/lib/local/ajaxClients/BasicAjaxClient";
import { GatedGetAjaxClient } from "@ms/uno-services/lib/local/ajaxClients/GatedGetAjaxClient";
import { GraphAjaxClient } from "@ms/uno-services/lib/local/ajaxClients/GraphAjaxClient";
import { GraphBatchQueueAjaxClient } from "@ms/uno-services/lib/local/ajaxClients/GraphBatchQueueAjaxClient";
import { ProjectHeaderAjaxClient } from "@ms/uno-services/lib/local/ajaxClients/ProjectHeaderAjaxClient";
import { RetryAjaxClient } from "@ms/uno-services/lib/local/ajaxClients/RetryAjaxClient";
import { TasksPlatformHeaderAjaxClient } from "@ms/uno-services/lib/local/ajaxClients/TasksPlatformTelemetryAjaxClient";
import { ThrottleAjaxClient } from "@ms/uno-services/lib/local/ajaxClients/ThrottleAjaxClient";
import { TokenRefreshAndRetryAjaxClient } from "@ms/uno-services/lib/local/ajaxClients/TokenRefreshAndRetryAjaxClient";
import { GraphODataApiVersion } from "@ms/uno-services/lib/local/services/graph/constants/GraphServiceConstants";
import { buildRetryAjaxClientWithCorrelation, getAjaxRetryConfig } from "@ms/uno-services/lib/local/utilities/ServiceUtilities";
/**
 * Class for managing setup of services for the app
 */
export class ProductionServiceOrchestrator extends BaseServiceOrchestrator {
    /**
     * Initialize an instance of the ProductionServiceOrchestrator
     * @param serviceFactory The service factory
     * @param requestPrioritizer The request prioritizer for priority based queuing
     * @param configProvider The configuration provider
     * @param loggers The logger context
     * @param getAccessTokenDelegate The delegate for getting an access token
     * @param refreshAccessTokenDelegate The delegate for refreshing an access token
     */
    constructor(serviceFactory, requestPrioritizer, configProvider, loggers, getAccessTokenDelegate, refreshAccessTokenDelegate) {
        const retrySettings = configProvider().settings.retrySettings;
        const { globalAjaxTimeout, maxRetryCount, maxRetryTimeout, defaultRetryDelay } = retrySettings;
        const ajaxClient = new RetryAjaxClient(new BasicAjaxClient(globalAjaxTimeout, loggers), getAjaxRetryConfig({
            maxRetryCount,
            maxRetryTimeoutInMiliseconds: maxRetryTimeout,
            delayOnRetryInMiliseconds: defaultRetryDelay,
        }), loggers);
        // Planner-only client: when the kill-switch is on, share the global client; otherwise build a
        // dedicated `RetryAjaxClient(CorrelationSettingAjaxClient(BasicAjaxClient))` so each transient
        // retry gets a fresh X-CorrelationId.
        const plannerAjaxClient = configProvider().changeGates.DisablePlannerXCorrelationId
            ? ajaxClient
            : buildRetryAjaxClientWithCorrelation(retrySettings, RequestHeaders.XCorrelationId, loggers);
        super(serviceFactory, requestPrioritizer, configProvider, loggers, ajaxClient, plannerAjaxClient, getAccessTokenDelegate, refreshAccessTokenDelegate);
        /** Batching ajax client for graph */
        this.graphAjaxClient = null;
    }
    /**
     * Get the AugLoop service provider
     */
    getAugloopServiceProvider() {
        const { augloop } = this.configProvider().settings.serviceConfigurations;
        return this.serviceFactory.getAugloopServiceProvider(async () => {
            return (await this.getAccessTokenDelegate(augloop.authResourceURI, undefined, augloop.authSilent)).accessToken;
        }, this.configProvider);
    }
    getCentroServiceProvider() {
        const { centro } = this.configProvider().settings.serviceConfigurations;
        const apiClient = new GatedGetAjaxClient(this.throttleClient(new TokenRefreshAndRetryAjaxClient(new AuthenticatedAjaxClient(this.ajaxClient, centro.authResourceURI, this.loggers, this.getAccessTokenDelegate, centro.authSilent), centro.authResourceURI, this.refreshAccessTokenDelegate, this.loggers, centro.authSilent)));
        return this.serviceFactory.getCentroServiceProvider(apiClient, this.configProvider);
    }
    /**
     * Get the ChatSvcAgg service provider
     */
    getChatSvcAggServiceProvider() {
        const { chatsvcagg } = this.configProvider().settings.serviceConfigurations;
        const apiClient = new GatedGetAjaxClient(this.throttleClient(new AuthenticatedAjaxClient(this.ajaxClient, chatsvcagg.authResourceURI, this.loggers, this.getAccessTokenDelegate, chatsvcagg.authSilent)));
        return this.serviceFactory.getChatSvcAggServiceProvider(apiClient, this.configProvider);
    }
    /**
     * Get the legacy ChatSvcAgg service provider
     */
    getLegacyChatSvcAggServiceProvider() {
        const { chatsvcagg } = this.configProvider().settings.serviceConfigurations;
        const apiClient = new GatedGetAjaxClient(this.throttleClient(new AuthenticatedAjaxClient(this.ajaxClient, chatsvcagg.authResourceURI, this.loggers, this.getAccessTokenDelegate, chatsvcagg.authSilent)));
        return this.serviceFactory.getLegacyChatSvcAggServiceProvider(apiClient, this.configProvider);
    }
    /**
     * Get the legacy Tcms service provider
     */
    getLegacyTcmsServiceProvider() {
        const apiClient = new GatedGetAjaxClient(this.throttleClient(this.ajaxClient));
        return this.serviceFactory.getLegacyTcmsServiceProvider(apiClient, this.requestPrioritizer, this.configProvider);
    }
    /**
     * Get the Conversations service provider
     */
    getConversationsServiceProvider() {
        return this.serviceFactory.getConversationsServiceProvider(this.getGraphAjaxClient(), this.requestPrioritizer, this.configProvider);
    }
    /**
     * Get the DriveItem service provider
     */
    getDriveItemServiceProvider() {
        return this.serviceFactory.getDriveItemServiceProvider(this.getGraphAjaxClient(), this.requestPrioritizer, this.configProvider);
    }
    /**
     * Get the Flow service provider
     */
    getFlowServiceProvider() {
        const { flow } = this.configProvider().settings.serviceConfigurations;
        const apiClient = new GatedGetAjaxClient(this.throttleClient(new TokenRefreshAndRetryAjaxClient(new AuthenticatedAjaxClient(this.ajaxClient, flow.authResourceURI, this.loggers, this.getAccessTokenDelegate, flow.authSilent), flow.authResourceURI, this.refreshAccessTokenDelegate, this.loggers, flow.authSilent)));
        return this.serviceFactory.getFlowServiceProvider(apiClient, this.configProvider);
    }
    /**
     * Get the Gateway service provider
     */
    getGatewayServiceProvider() {
        const { project } = this.configProvider().settings.serviceConfigurations;
        const apiClient = new GatedGetAjaxClient(this.throttleClient(new ProjectHeaderAjaxClient(new TokenRefreshAndRetryAjaxClient(new AuthenticatedAjaxClient(this.ajaxClient, project.authResourceURI, this.loggers, this.getAccessTokenDelegate, project.authSilent), project.authResourceURI, this.refreshAccessTokenDelegate, this.loggers, project.authSilent), this.configProvider)));
        return this.serviceFactory.getGatewayServiceProvider(apiClient, this.configProvider);
    }
    /**
     * Get the Publishing service provider
     */
    getPublishingServiceProvider() {
        const { retailservices } = this.configProvider().settings.serviceConfigurations;
        return this.serviceFactory.getPublishingServiceProvider(() => {
            return this.getAccessTokenDelegate(retailservices.authResourceURI, undefined, retailservices.authSilent);
        }, this.configProvider);
    }
    /**
     * Get the Groups service provider
     */
    getGroupsServiceProvider() {
        return this.serviceFactory.getGroupsServiceProvider(this.getGraphAjaxClient(), this.requestPrioritizer, this.configProvider);
    }
    /**
     * Get the ModernGroup service provider
     */
    getModernGroupServiceProvider() {
        return this.serviceFactory.getModernGroupServiceProvider(this.getGraphAjaxClient(), this.requestPrioritizer, this.configProvider);
    }
    /**
     * Get the iris service provider
     */
    getIrisServiceProvider() {
        const { iris } = this.configProvider().settings.serviceConfigurations;
        return this.serviceFactory.getIrisServiceProvider(new GatedGetAjaxClient(this.throttleClient(new TokenRefreshAndRetryAjaxClient(new AuthenticatedAjaxClient(this.ajaxClient, iris.authResourceURI, this.loggers, this.getAccessTokenDelegate, iris.authSilent), iris.authResourceURI, this.refreshAccessTokenDelegate, this.loggers, iris.authSilent))), this.requestPrioritizer, this.configProvider);
    }
    /**
     * Get the loop service provider
     */
    getLoopServiceProvider() {
        const { settings } = this.configProvider();
        const { loop } = settings.serviceConfigurations;
        // Correlation sits *inside* the retry stack (RetryAjaxClient(CorrelationSettingAjaxClient(BasicAjaxClient)))
        // so every transient retry re-stamps a fresh MS-CV.
        const apiClient = this.throttleClient(new TokenRefreshAndRetryAjaxClient(new AuthenticatedAjaxClient(buildRetryAjaxClientWithCorrelation(settings.retrySettings, LoopServiceRequestHeaders.MsCv, this.loggers), loop.authResourceURI, this.loggers, this.getAccessTokenDelegate, loop.authSilent), loop.authResourceURI, this.refreshAccessTokenDelegate, this.loggers, loop.authSilent));
        return this.serviceFactory.getLoopServiceProvider(apiClient, this.configProvider);
    }
    /**
     * Get the Loop MTS service provider
     */
    getLoopMtsServiceProvider() {
        const { loopMts } = this.configProvider().settings.serviceConfigurations;
        const apiClient = new GatedGetAjaxClient(this.throttleClient(new TokenRefreshAndRetryAjaxClient(new AuthenticatedAjaxClient(this.ajaxClient, loopMts.authResourceURI, this.loggers, this.getAccessTokenDelegate, loopMts.authSilent), loopMts.authResourceURI, this.refreshAccessTokenDelegate, this.loggers, loopMts.authSilent)));
        return this.serviceFactory.getLoopMtsServiceProvider(apiClient, this.configProvider);
    }
    /**
     * Get the MARS service provider
     */
    getMarsServiceProvider() {
        const { mars } = this.configProvider().settings.serviceConfigurations;
        const { retrySettings } = this.configProvider().settings;
        // Correlation sits *inside* the retry stack so every transient retry re-stamps a fresh correlation id.
        const apiClient = new GatedGetAjaxClient(this.throttleClient(new TokenRefreshAndRetryAjaxClient(new AuthenticatedAjaxClient(buildRetryAjaxClientWithCorrelation(retrySettings, MarsServiceRequestHeaders.CorrelationId, this.loggers), mars.authResourceURI, this.loggers, this.getAccessTokenDelegate, mars.authSilent), mars.authResourceURI, this.refreshAccessTokenDelegate, this.loggers, mars.authSilent)));
        return this.serviceFactory.getMarsServiceProvider(apiClient, this.configProvider);
    }
    /**
     * Get the Mru service provider
     */
    getMruServiceProvider() {
        const { mru } = this.configProvider().settings.serviceConfigurations;
        const apiClient = new GatedGetAjaxClient(this.throttleClient(new TokenRefreshAndRetryAjaxClient(new AuthenticatedAjaxClient(this.ajaxClient, mru.authResourceURI, this.loggers, this.getAccessTokenDelegate, mru.authSilent), mru.authResourceURI, this.refreshAccessTokenDelegate, this.loggers, mru.authSilent)));
        return this.serviceFactory.getMruServiceProvider(apiClient, this.configProvider);
    }
    /**
     * Get the Ocps service provider
     */
    getOcpsServiceProvider() {
        const { ocps } = this.configProvider().settings.serviceConfigurations;
        const apiClient = new GatedGetAjaxClient(this.throttleClient(new TokenRefreshAndRetryAjaxClient(new AuthenticatedAjaxClient(this.ajaxClient, ocps.authResourceURI, this.loggers, this.getAccessTokenDelegate, ocps.authSilent), ocps.authResourceURI, this.refreshAccessTokenDelegate, this.loggers, ocps.authSilent)));
        return this.serviceFactory.getOcpsServiceProvider(apiClient, this.requestPrioritizer, this.configProvider);
    }
    /**
     * Get the oneShell service provider
     */
    getOneShellServiceProvider() {
        const { oneshell } = this.configProvider().settings.serviceConfigurations;
        const apiClient = new GatedGetAjaxClient(this.throttleClient(new TokenRefreshAndRetryAjaxClient(new AuthenticatedAjaxClient(this.ajaxClient, oneshell.authResourceURI, this.loggers, this.getAccessTokenDelegate, oneshell.authSilent), oneshell.authResourceURI, this.refreshAccessTokenDelegate, this.loggers, oneshell.authSilent)));
        return this.serviceFactory.getOneShellServiceProvider(apiClient, this.requestPrioritizer, this.configProvider);
    }
    /**
     * Get the legacy Planner graph service provider
     */
    getPlannerGraphServiceProvider() {
        return this.serviceFactory.getPlannerGraphServiceProvider(new TasksPlatformHeaderAjaxClient(this.getGraphAjaxClient(), this.configProvider, ClientSurface.Evo), this.requestPrioritizer, this.configProvider);
    }
    /**
     * Get the legacy ProjectLandingPage service provider
     */
    getLegacyPlpServiceProvider() {
        const { project } = this.configProvider().settings.serviceConfigurations;
        const apiClient = new GatedGetAjaxClient(this.throttleClient(new ProjectHeaderAjaxClient(new TokenRefreshAndRetryAjaxClient(new AuthenticatedAjaxClient(this.ajaxClient, project.authResourceURI, this.loggers, this.getAccessTokenDelegate, project.authSilent), project.authResourceURI, this.refreshAccessTokenDelegate, this.loggers, project.authSilent), this.configProvider)));
        return this.serviceFactory.getLegacyPlpServiceProvider(apiClient, this.configProvider);
    }
    /**
     * Get the new ProjectLandingPage service provider
     */
    getProjectLandingPageServiceProvider() {
        const { project } = this.configProvider().settings.serviceConfigurations;
        const apiClient = new GatedGetAjaxClient(this.throttleClient(new ProjectHeaderAjaxClient(new TokenRefreshAndRetryAjaxClient(new AuthenticatedAjaxClient(this.ajaxClient, project.authResourceURI, this.loggers, this.getAccessTokenDelegate, project.authSilent), project.authResourceURI, this.refreshAccessTokenDelegate, this.loggers, project.authSilent), this.configProvider)));
        return this.serviceFactory.getProjectLandingPageServiceProvider(apiClient, this.configProvider);
    }
    /**
     * Get the ProjectScheduling service provider
     */
    getProjectSchedulingServiceProvider() {
        const { project } = this.configProvider().settings.serviceConfigurations;
        const apiClient = new GatedGetAjaxClient(this.throttleClient(new ProjectHeaderAjaxClient(new TokenRefreshAndRetryAjaxClient(new AuthenticatedAjaxClient(this.ajaxClient, project.authResourceURI, this.loggers, this.getAccessTokenDelegate, project.authSilent), project.authResourceURI, this.refreshAccessTokenDelegate, this.loggers, project.authSilent), this.configProvider)));
        return this.serviceFactory.getProjectSchedulingServiceProvider(apiClient, this.requestPrioritizer, this.configProvider);
    }
    /**
     * Get the Retail service provider
     */
    getRetailServiceProvider() {
        const { retailservices } = this.configProvider().settings.serviceConfigurations;
        const { retrySettings } = this.configProvider().settings;
        // Correlation sits *inside* the retry stack so every transient retry re-stamps a fresh client-request-id.
        const apiClient = new GatedGetAjaxClient(this.throttleClient(new TokenRefreshAndRetryAjaxClient(new AuthenticatedAjaxClient(buildRetryAjaxClientWithCorrelation(retrySettings, RequestHeaders.ClientRequestId, this.loggers), retailservices.authResourceURI, this.loggers, this.getAccessTokenDelegate, retailservices.authSilent), retailservices.authResourceURI, this.refreshAccessTokenDelegate, this.loggers, retailservices.authSilent)));
        return this.serviceFactory.getRetailServiceProvider(apiClient, this.requestPrioritizer, this.configProvider);
    }
    /**
     * Get the Roaming service provider
     */
    getRoamingServiceProvider() {
        const { roaming } = this.configProvider().settings.serviceConfigurations;
        const apiClient = new GatedGetAjaxClient(this.throttleClient(new AuthenticatedAjaxClient(this.ajaxClient, roaming.authResourceURI, this.loggers, this.getAccessTokenDelegate, roaming.authSilent)));
        return this.serviceFactory.getRoamingServiceProvider(apiClient, this.configProvider);
    }
    /**
     * Get the SiteURL service provider
     */
    getSiteURLServiceProvider() {
        return this.serviceFactory.getSiteURLServiceProvider(this.getGraphAjaxClient(), this.requestPrioritizer, this.configProvider);
    }
    /**
     * Get the SubstrateSearch service provider
     */
    getSubstrateSearchServiceProvider() {
        const { substrateSearch } = this.configProvider().settings.serviceConfigurations;
        const apiClient = new GatedGetAjaxClient(this.throttleClient(new TokenRefreshAndRetryAjaxClient(new AuthenticatedAjaxClient(this.ajaxClient, substrateSearch.authResourceURI, this.loggers, this.getAccessTokenDelegate, substrateSearch.authSilent), substrateSearch.authResourceURI, this.refreshAccessTokenDelegate, this.loggers, substrateSearch.authSilent)));
        return this.serviceFactory.getSubstrateSearchServiceProvider(apiClient, this.requestPrioritizer, this.configProvider);
    }
    /**
     * Get the Tcms service provider
     */
    getTcmsServiceProvider() {
        const apiClient = new GatedGetAjaxClient(this.throttleClient(this.ajaxClient));
        return this.serviceFactory.getTcmsServiceProvider(apiClient, this.requestPrioritizer, this.configProvider);
    }
    /**
     * Get the Teams service provider
     */
    getTeamsServiceProvider() {
        return this.serviceFactory.getTeamsServiceProvider(this.getGraphAjaxClient(), this.requestPrioritizer, this.configProvider);
    }
    /**
     * Get the Tenant service provider
     */
    getTenantServiceProvider() {
        return this.serviceFactory.getTenantServiceProvider(this.getGraphAjaxClient(), this.requestPrioritizer, this.configProvider);
    }
    /**
     * Get the Planner Tenant service provider
     */
    getPlannerTenantServiceProvider() {
        return this.serviceFactory.getPlannerTenantServiceProvider(this.getGraphAjaxClient(), this.requestPrioritizer, this.configProvider);
    }
    /**
     * Get the Todo service provider
     */
    getTodoServiceProvider() {
        const { stapi } = this.configProvider().settings.serviceConfigurations;
        const tokenRefreshClient = new TokenRefreshAndRetryAjaxClient(new AuthenticatedAjaxClient(this.ajaxClient, stapi.authResourceURI, this.loggers, this.getAccessTokenDelegate, stapi.authSilent), stapi.authResourceURI, this.refreshAccessTokenDelegate, this.loggers, stapi.authSilent);
        // Todo stays on the existing `DisableServiceThrottling` gate (pre-Bug 3065174 behavior),
        // matching Planner; the newer EVO services share `DisableEvoServiceThrottling` instead.
        const throttledClient = this.configProvider().changeGates.DisableServiceThrottling
            ? tokenRefreshClient
            : new ThrottleAjaxClient(tokenRefreshClient, this.configProvider().settings.throttleSettings, this.loggers);
        const apiClient = new GatedGetAjaxClient(throttledClient);
        return this.serviceFactory.getTodoServiceProvider(apiClient, this.configProvider);
    }
    /**
     * Get the Microsoft Teams service provider
     */
    getMsTeamsServiceProvider() {
        const { teams } = this.configProvider().settings.serviceConfigurations;
        const { retrySettings } = this.configProvider().settings;
        // Correlation sits *inside* the retry stack so every transient retry re-stamps a fresh client-request-id.
        const apiClient = new GatedGetAjaxClient(this.throttleClient(new TokenRefreshAndRetryAjaxClient(new AuthenticatedAjaxClient(buildRetryAjaxClientWithCorrelation(retrySettings, RequestHeaders.ClientRequestId, this.loggers), teams.authResourceURI, this.loggers, this.getAccessTokenDelegate, teams.authSilent), teams.authResourceURI, this.refreshAccessTokenDelegate, this.loggers, teams.authSilent)));
        return this.serviceFactory.getMsTeamsServiceProvider(apiClient, this.requestPrioritizer, this.configProvider);
    }
    /**
     * Get the User service provider
     */
    getUserServiceProvider() {
        return this.serviceFactory.getUserServiceProvider(this.getGraphAjaxClient(), this.requestPrioritizer, this.configProvider);
    }
    /**
     * Get the Search service provider
     */
    getSearchServiceProvider() {
        return this.serviceFactory.getSearchServiceProvider(this.getGraphAjaxClient(), this.requestPrioritizer, this.configProvider);
    }
    /**
     * Get the ODSP service provider
     */
    getOdspServiceProvider() {
        const { odsp } = this.configProvider().settings.serviceConfigurations;
        const { retrySettings } = this.configProvider().settings;
        // Correlation sits *inside* the retry stack so every transient retry re-stamps a fresh client-request-id.
        const apiClient = new GatedGetAjaxClient(this.throttleClient(new TokenRefreshAndRetryAjaxClient(new AuthenticatedAjaxClient(buildRetryAjaxClientWithCorrelation(retrySettings, RequestHeaders.ClientRequestId, this.loggers), odsp.authResourceURI, this.loggers, this.getAccessTokenDelegate, odsp.authSilent), odsp.authResourceURI, this.refreshAccessTokenDelegate, this.loggers, odsp.authSilent)));
        return this.serviceFactory.getOdspServiceProvider(apiClient, this.requestPrioritizer, this.configProvider);
    }
    /**
     * Get the Trouter service provider
     */
    getTrouterServiceProvider() {
        return this.serviceFactory.getTrouterServiceProvider(this.getAccessTokenDelegate, this.refreshAccessTokenDelegate, this.loggers, this.configProvider);
    }
    /**
     * Get the Graph ajax client
     */
    getGraphAjaxClient() {
        if (this.graphAjaxClient != null) {
            return this.graphAjaxClient;
        }
        const { graph } = this.configProvider().settings.serviceConfigurations;
        const retrySettings = this.configProvider().settings.retrySettings;
        // Build the inner ajax client so correlation sits *inside* RetryAjaxClient — i.e.
        // `GraphAjaxClient(RetryAjaxClient(CorrelationSettingAjaxClient(BasicAjaxClient)))`. This way every
        // transient retry re-enters CorrelationSettingAjaxClient and stamps a fresh client-request-id,
        // matching the per-attempt correlation contract used by the Planner chain.
        let ajaxClient = new GraphAjaxClient(buildRetryAjaxClientWithCorrelation(retrySettings, RequestHeaders.ClientRequestId, this.loggers));
        // Add auth client
        ajaxClient = new TokenRefreshAndRetryAjaxClient(new AuthenticatedAjaxClient(ajaxClient, graph.authResourceURI, this.loggers, this.getAccessTokenDelegate, graph.authSilent), graph.authResourceURI, this.refreshAccessTokenDelegate, this.loggers, graph.authSilent);
        // Add throttling client to limit concurrency and circuit-break on 429s
        if (!this.configProvider().changeGates.DisableGraphServiceThrottling) {
            ajaxClient = new ThrottleAjaxClient(ajaxClient, this.configProvider().settings.throttleSettings, this.loggers);
        }
        // Use Gated Get Ajax Client to gate all GET calls and avoid duplicate concurrent requests
        const gatedAjaxClient = new GatedGetAjaxClient(ajaxClient);
        const batchAppendHeaders = [
            GraphResponseHeaders.ClientRequestId,
            GraphResponseHeaders.RequestId,
            GraphResponseHeaders.TCorrelationId,
        ];
        /** Wrap with auto-batching client if batching is turned on */
        if (graph.batchAll) {
            this.graphAjaxClient = new GraphBatchQueueAjaxClient(gatedAjaxClient, GraphODataApiVersion.Beta, this.loggers, batchAppendHeaders, this.configProvider);
        }
        else {
            this.graphAjaxClient = gatedAjaxClient;
        }
        return this.graphAjaxClient;
    }
}
